<template>
  <div v-if="visible" class="war-room-commission-tooltip">
    <div ref="back" class="war-room-commission-tooltip__back" @click="handleClose" />

    <div v-if="!videoOpened" class="war-room-commission-tooltip__tooltip" :style="{ left: `${tooltipLeft}px` }">
      <RectIcon class="war-room-commission-tooltip__tooltip-rect-icon" />
      <div class="war-room-commission-tooltip__tooltip-wrapper">
        <div class="war-room-commission-tooltip__tooltip-title">Off to a great start!</div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="war-room-commission-tooltip__tooltip-desc" v-html="hintText" />
        <div class="war-room-commission-tooltip__tooltip-cta" @click="handleOpenVideo">
          <VideoIcon class="war-room-commission-tooltip__tooltip-video-icon" />
          Watch Video
        </div>
        <div class="war-room-commission-tooltip__icon-wrapper" @click="handleClose">
          <CloseIcon class="war-room-commission-tooltip__tooltip-close-icon" />
        </div>
      </div>
    </div>

    <div v-else class="war-room-commission-tooltip__video-wrapper" @click="handleClose">
      <div class="war-room-commission-tooltip__video">
        <CloseVideoIcon width="32px" height="32px" class="war-room-commission-tooltip__video-close" />
        <div class="war-room-commission-tooltip__player">
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/BDF-Jkrs5BY?rel=0&showinfo=0&autoplay=1&enablejsapi=1"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            class="war-room-commission-tooltip__frame"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-ignore
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { defineComponent } from '@nuxtjs/composition-api';
import { mapMutations, mapState } from 'vuex';
import RectIcon from './images/rect.vue.svg';
import VideoIcon from './images/video.vue.svg';
import CloseIcon from './images/close.vue.svg';
import CloseVideoIcon from '@/assets/svg/close.vue.svg';
import { WAR_ROOM_COMMISSION_TOOLTIP as TOOLTIP_NAME } from '@/components/common/constants';
import CommissionBalanceQuery from '@/apollo/queries/CommissionBalance.query';
import type { CommissionBalance } from '@/types/graphql';

const BALANCE_CLASS = '.header-commission-balance';

export default defineComponent({
  name: 'WarRoomCommissionTooltip',
  components: {
    RectIcon,
    VideoIcon,
    CloseIcon,
    CloseVideoIcon,
  },
  apollo: {
    commissionBalance: {
      query: CommissionBalanceQuery,
      skip() {
        return !this.$auth.loggedIn;
      },
    },
  },
  data(): {
    commissionBalance: CommissionBalance;
    interval: NodeJS.Timeout | null;
    tooltipLeft: number;
    videoOpened: boolean;
  } {
    return {
      commissionBalance: {
        currentBalance: 0,
        delta: 0,
      },
      interval: null,
      tooltipLeft: 0,
      videoOpened: false,
    };
  },
  computed: {
    ...mapState('ui', ['onboardingTooltips', 'isCommissionDropdownOpen']),
    visible(): boolean {
      return this.onboardingTooltips[TOOLTIP_NAME];
    },
    balance(): number {
      return this.commissionBalance.currentBalance;
    },
    referralCount(): number {
      return this.$auth.user?.warRoomReferralCount || 0;
    },
    hintText(): string {
      const referralHint = this.referralCount
        ? `, additionally <span class="war-room-commission-tooltip__tooltip-desc--emph">${this.referralCount} of your referrals</span> just signed up`
        : '';

      return `You've already earned <span class="war-room-commission-tooltip__tooltip-desc--emph">${this.balance} Commission</span> for signing up for the War Room${referralHint}! Curious about what Commission is and what you can do with it? Check out the video below.`;
    },
  },
  watch: {
    isCommissionDropdownOpen(newVal: boolean) {
      if (this.visible && newVal) {
        this.handleClose();
      }
    },
  },
  beforeMount() {
    if (this.$tooltip.isPresent(TOOLTIP_NAME)) {
      return;
    }

    this.interval = setInterval(() => {
      if (document.querySelector(BALANCE_CLASS)) {
        this.calculatePosition();
        window.addEventListener('resize', this.calculatePosition);

        disableBodyScroll(this.$refs.window);

        window.scrollTo(0, 0);
        if (this.interval) clearInterval(this.interval);
        this.$analytics.logEvent('war-room__common__earn-commission-tooltip__shown');
      }
    }, 100);
  },
  beforeDestroy() {
    this.handleDestroy();
  },
  methods: {
    ...mapMutations('ui', ['closeOnboardingTooltip']),
    calculatePosition() {
      const ratingEl = document.querySelector(BALANCE_CLASS);
      if (ratingEl) {
        const clientRect = ratingEl.getBoundingClientRect();
        this.tooltipLeft = clientRect.left + clientRect.width / 2 - 264 + 38;
      }
    },
    handleClose() {
      this.closeOnboardingTooltip(TOOLTIP_NAME);
      this.$tooltip.close(TOOLTIP_NAME);

      this.handleDestroy();
    },
    handleDestroy() {
      window.removeEventListener('resize', this.calculatePosition);
      if (this.interval) clearInterval(this.interval);
      clearAllBodyScrollLocks();
    },
    handleOpenVideo() {
      this.videoOpened = true;
      this.$analytics.logEvent('war-room__common__earn-commission-tooltip-video-button__click');
    },
  },
});
</script>

<style lang="scss" scoped>
.war-room-commission-tooltip {
  position: absolute;
  left: 0;
  top: 50px;
  bottom: 0;
  right: 0;
  z-index: 1000;

  &__back {
    position: absolute;
    left: 0;
    top: -2px;
    right: 0;
    bottom: 0;
    background-color: var(--theme-color-background);
    opacity: 0.8;
  }

  &__tooltip {
    position: absolute;
    top: -12px;
    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.15));
  }

  &__tooltip-rect-icon {
    position: absolute;
    top: 0;
    right: 0;
    color: var(--theme-color-card);
  }

  &__tooltip-wrapper {
    background-color: var(--theme-color-card);
    position: relative;
    top: 8px;
    width: 264px;
    padding: 16px 32px 16px 16px;
    border-radius: 8px;
  }

  &__tooltip-title {
    @include body-semibold;

    color: var(--theme-color-text);
    margin-bottom: 5px;
  }

  &__tooltip-desc {
    @include subhead;

    color: var(--theme-color-text);
    margin-bottom: 7px;
  }

  ::v-deep &__tooltip-desc--emph {
    font-weight: bold;
  }

  &__tooltip-cta {
    @include subhead-semibold;

    display: flex;
    align-items: center;
    color: var(--blue-60);
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: var(--blue-50);
    }
  }

  &__tooltip-video-icon {
    margin-right: 7px;
  }

  &__icon-wrapper {
    width: 32px;
    height: 32px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    position: absolute;
    right: 4px;
    top: 4px;
  }

  &__tooltip-close-icon {
    color: var(--theme-color-text);
  }

  &__video-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__video {
    width: 90%;
    max-width: 1024px;
    max-height: 577px;
    line-height: 0;
    position: relative;
  }

  &__player {
    @include aspect-ratio-container(calc(9 / 16));

    height: 0;
    position: relative;
    line-height: 0;
  }

  &__frame {
    @include aspect-ratio-content;
  }

  &__video-close {
    position: absolute;
    top: -40px;
    right: 0;
    cursor: pointer;
    color: var(--theme-color-text);
    transition: color 0.2s;

    &:hover {
      color: var(--theme-color-sub-text);
    }
  }
}
</style>
